import React, { useState, createElement } from 'react';
import { Button } from './ui/Button';
import { CheckCircle2, AlertCircle } from 'lucide-react';
// EmailJS Configuration
// To set this up:
// 1. Go to https://www.emailjs.com/ and create a free account
// 2. Add an email service (Gmail, Outlook, etc.)
// 3. Create an email template with these variables: {{from_name}}, {{from_email}}, {{phone}}, {{company}}
// 4. Replace the values below with your actual IDs from EmailJS dashboard
const EMAILJS_SERVICE_ID = 'YOUR_SERVICE_ID'; // e.g., 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID'; // e.g., 'template_xyz789'
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY'; // e.g., 'abcdefghijk123456'
export function ContactForm({
  id = 'contact-form'
}: {
  id?: string;
}) {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setErrors({});
    const formData = new FormData(e.currentTarget);
    const data = {
      from_name: formData.get('name') as string,
      from_email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      company: formData.get('company') as string,
      to_email: 'hacktime@ukr.net' // Your email address
    };
    try {
      // Load EmailJS script dynamically
      if (typeof window !== 'undefined' && !(window as any).emailjs) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
        script.async = true;
        document.head.appendChild(script);
        await new Promise(resolve => {
          script.onload = resolve;
        });
        (window as any).emailjs.init(EMAILJS_PUBLIC_KEY);
      }
      // Send email via EmailJS
      const response = await (window as any).emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, data);
      if (response.status === 200) {
        setFormState('success');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setFormState('error');
      setErrors({
        submit: 'Failed to send message. Please try again or contact us directly.'
      });
    }
  };
  if (formState === 'success') {
    return <div className="bg-white p-8 rounded-xl shadow-xl text-center h-full flex flex-col items-center justify-center min-h-[400px]" data-id="element-30">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-31">
          <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-32" />
        </div>
        <h3 className="text-2xl font-bold text-[#0A2540] mb-2" data-id="element-33">
          Request Received!
        </h3>
        <p className="text-gray-600 mb-6" data-id="element-34">
          Our marine marketing strategists will review your dealership's needs
          and contact you shortly.
        </p>
        <Button variant="outline" onClick={() => setFormState('idle')} data-id="element-35">
          Send Another Request
        </Button>
      </div>;
  }
  return <div className="bg-white p-6 md:p-8 rounded-xl shadow-2xl border-t-4 border-[#D4AF37]" data-id="element-36">
      <h3 className="text-2xl font-bold text-[#0A2540] mb-2" data-id="element-37">
        Get Your Free Strategy Call
      </h3>

      {formState === 'error' && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-38">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-39" />
          <div data-id="element-40">
            <p className="text-sm text-red-800 font-medium" data-id="element-41">
              Unable to send message
            </p>
            <p className="text-xs text-red-600 mt-1" data-id="element-42">{errors.submit}</p>
          </div>
        </div>}

      <form id={id} onSubmit={handleSubmit} className="space-y-4" data-id="element-43">
        <div data-id="element-44">
          <label htmlFor={`${id}-name`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-45">
            Full Name
          </label>
          <input type="text" id={`${id}-name`} name="name" required className="text-gray-900 w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0A2540] focus:border-transparent outline-none transition-all" placeholder="John Doe" data-id="element-46" />
        </div>

        <div data-id="element-47">
          <label htmlFor={`${id}-email`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-48">
            Work Email
          </label>
          <input type="email" id={`${id}-email`} name="email" required className="text-gray-900 w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0A2540] focus:border-transparent outline-none transition-all" placeholder="john@dealership.com" data-id="element-49" />
        </div>

        <div data-id="element-50">
          <label htmlFor={`${id}-phone`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-51">
            Phone Number
          </label>
          <input type="tel" id={`${id}-phone`} name="phone" required className="text-gray-900 w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0A2540] focus:border-transparent outline-none transition-all" placeholder="(555) 123-4567" data-id="element-52" />
        </div>

        <div data-id="element-53">
          <label htmlFor={`${id}-company`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-54">
            Dealership Name
          </label>
          <input type="text" id={`${id}-company`} name="company" required className="text-gray-900 w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0A2540] focus:border-transparent outline-none transition-all" placeholder="Bay Marine Sales" data-id="element-55" />
        </div>

        <Button type="submit" variant="primary" className="w-full text-lg shadow-lg shadow-gold/20" isLoading={formState === 'submitting'} disabled={formState === 'submitting'} data-id="element-56">
          Book Strategy Call
        </Button>

        <p className="text-xs text-center text-gray-500 mt-4" data-id="element-57">
          We respect your privacy.
        </p>
      </form>
    </div>;
}